import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';

export default function AvatarUpload({ userId, avatarUrl, initials, onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp']?.includes(file?.type)) {
      setError('Only JPG, PNG, or WebP images are allowed.');
      return;
    }
    if (file?.size > 2 * 1024 * 1024) {
      setError('Image must be under 2MB.');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/upload/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploadSuccess?.(data?.url);
    } catch (err) {
      setError(err?.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const file = e?.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const handleInputChange = (e) => {
    const file = e?.target?.files?.[0];
    handleFile(file);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Avatar Preview */}
      <div className="relative">
        <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[var(--color-border)] shadow-[var(--shadow-md)] bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center">
          {avatarUrl ? (
            <img src={avatarUrl} alt="Profile avatar" className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl font-bold text-white font-[var(--font-heading)]">{initials}</span>
          )}
        </div>
        {uploading && (
          <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef?.current?.click()}
        className={`w-full max-w-xs border-2 border-dashed rounded-[var(--radius-md)] p-4 text-center cursor-pointer transition-all duration-200 ${
          dragOver
            ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
            : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/50 hover:bg-[var(--color-muted)]'
        }`}
      >
        <Icon name="Upload" size={20} color="var(--color-muted-foreground)" className="mx-auto mb-1" />
        <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
          {uploading ? 'Uploading...' : 'Drop photo here or click to browse'}
        </p>
        <p className="text-xs text-[var(--color-muted-foreground)]/60 mt-0.5">JPG, PNG, WebP · Max 2MB</p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleInputChange}
      />

      {error && (
        <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)] text-center">{error}</p>
      )}
    </div>
  );
}
